import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import RefreshIcon from "@mui/icons-material/Refresh";
import { query } from "../api/client";
import { useResource } from "../hooks/useResource";
import { EmptyState, ErrorBanner, LoadingState, PageHeader, ago } from "../components/common";
import { OutcomeChip } from "./OutcomeChip";
import { PortalOutcomeDialog } from "./PortalOutcomeDialog";
import { FALLBACK_OUTCOMES, type PortalLead, type PortalLeadPage, type PortalSummary } from "./types";

/**
 * The client's own feed.
 *
 * Filters live in the URL for the same reason they do in the console: a client
 * with two people working the list can send "the ones nobody has answered for" as
 * a link. There is deliberately no client selector — the session is the scope, and
 * the API refuses a request that tries to name one.
 */

export function PortalLeadsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");
  const [reporting, setReporting] = useState<PortalLead | null>(null);

  const get = (key: string, fallback = "") => params.get(key) ?? fallback;
  const limit = Number(get("limit", "50")) || 50;
  const offset = Number(get("offset", "0")) || 0;

  const set = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    // Any filter change invalidates the page cursor.
    if (!("offset" in changes)) next.delete("offset");
    setParams(next, { replace: true });
  };

  const path = useMemo(
    () =>
      `/api/portal/leads${query({
        // Passed through without a control of its own. The API accepts it and the
        // filters here are the URL, so a link carrying `?signal=` has to keep
        // working — dropping it silently would answer a narrowed link with the
        // whole feed.
        signal: get("signal"),
        outcome: get("outcome"),
        awaiting: get("awaiting"),
        maxDaysOld: get("maxDaysOld"),
        q: get("q"),
        sort: get("sort", "assigned_at"),
        dir: get("dir", "desc"),
        limit,
        offset,
      })}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.toString()],
  );

  const feed = useResource<PortalLeadPage>(path);
  // Only for the outcome vocabulary — the server's list, so the filter cannot
  // offer a value the API would reject.
  const summary = useResource<PortalSummary>("/api/portal/summary");
  const outcomes = summary.data?.outcomes ?? FALLBACK_OUTCOMES;

  const sortKey = get("sort", "assigned_at");
  const direction = get("dir", "desc") === "asc" ? "asc" : "desc";
  const toggleSort = (key: string) => {
    if (key === sortKey) set({ dir: direction === "asc" ? "desc" : "asc" });
    else set({ sort: key, dir: "desc" });
  };

  return (
    <Box>
      <PageHeader
        title="Leads"
        subtitle="Every lead routed to your account. Tell us what happened to each one — that is what the results are built from."
        actions={
          <Button startIcon={<RefreshIcon />} onClick={feed.reload}>
            Refresh
          </Button>
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" alignItems="center" sx={{ gap: 1.5 }}>
          <TextField
            label="Search address, owner or reason"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") set({ q: search.trim() });
            }}
            onBlur={() => set({ q: search.trim() })}
            sx={{ minWidth: 280, flexGrow: 1 }}
          />
          <TextField
            select
            label="Outcome"
            value={get("outcome")}
            onChange={(e) => set({ outcome: e.target.value })}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Any outcome</MenuItem>
            {outcomes.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Max age (days)"
            type="number"
            value={get("maxDaysOld")}
            onChange={(e) => set({ maxDaysOld: e.target.value })}
            sx={{ width: 150 }}
            helperText="Excludes undated leads"
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={get("awaiting") === "true"}
                onChange={(e) => set({ awaiting: e.target.checked ? "true" : null })}
              />
            }
            label="Needs a reply from me"
          />
        </Stack>
      </Paper>

      <ErrorBanner error={feed.error} onRetry={feed.reload} />

      {feed.loading && !feed.data ? <LoadingState label="Loading your leads…" /> : null}

      {feed.data && feed.data.items.length === 0 ? (
        <EmptyState
          title="No leads match these filters"
          hint="Clear a filter to see everything routed to your account."
        />
      ) : null}

      {feed.data && feed.data.items.length > 0 ? (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "assigned_at"}
                      direction={direction}
                      onClick={() => toggleSort("assigned_at")}
                    >
                      Received
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Why now</TableCell>
                  <TableCell>Who to call</TableCell>
                  <TableCell align="right">Units</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortKey === "event_at"}
                      direction={direction}
                      onClick={() => toggleSort("event_at")}
                    >
                      Event
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {feed.data.items.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/portal/leads/${row.id}`)}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{ago(row.assignedAt)}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" noWrap title={row.address ?? ""}>
                        {row.address ?? "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[row.city ?? row.borough, row.state, row.zip].filter(Boolean).join(" · ") || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 340 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                        {row.whyNow ?? row.signalLabel ?? row.signalKey}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={row.org ?? ""}>
                        {row.org ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.resUnits ?? "—"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.eventAt ? row.eventAt.slice(0, 10) : "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.daysOld === null ? "undated" : `${row.daysOld}d old`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <OutcomeChip outcome={row.outcome} />
                    </TableCell>
                    <TableCell align="right">
                      {/* Stops the row's navigation: reporting from the list is the
                          point, and being thrown onto the detail screen first is
                          what makes people stop doing it. */}
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setReporting(row);
                        }}
                      >
                        {row.outcome ? "Update" : "Report"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={feed.data.total}
            page={Math.floor(offset / limit)}
            rowsPerPage={limit}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(_e, page) => set({ offset: String(page * limit) })}
            onRowsPerPageChange={(e) => set({ limit: e.target.value, offset: "0" })}
          />
        </Paper>
      ) : null}

      {reporting ? (
        <PortalOutcomeDialog
          lead={reporting}
          outcomes={outcomes}
          onClose={() => setReporting(null)}
          onSaved={() => {
            setReporting(null);
            feed.reload();
            // The overview's counters are derived from the same rows.
            summary.reload();
          }}
        />
      ) : null}
    </Box>
  );
}
